import type { BundledLanguage, ShikiTransformer } from "shiki";
import { codeToHtml } from "shiki";
import type { ComponentProps, HTMLAttributes } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { cn } from "./lib/cn";
import { parseError } from "./lib/error";
import { Button } from "./ui/button";

type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: BundledLanguage;
  showLineNumbers?: boolean;
};
const CodeBlockContext = createContext({ code: "" });
const lineNumberTransformer: ShikiTransformer = {
  name: "line-numbers",
  line(node, line) {
    node.children.unshift({
      type: "element",
      tagName: "span",
      properties: {
        className: [
          "inline-block",
          "min-w-10",
          "mr-4",
          "text-right",
          "select-none",
          "text-muted-foreground",
        ],
      },
      children: [{ type: "text", value: String(line) }],
    });
  },
};

/** Highlights code with Shiki, keeping the upstream async/lazy rendering contract. */
/** Produces light and dark Shiki HTML for a fenced code block. */
export async function highlightCode(
  code: string,
  language: BundledLanguage,
  showLineNumbers = false,
) {
  const transformers = showLineNumbers ? [lineNumberTransformer] : [];
  return Promise.all([
    codeToHtml(code, { lang: language, theme: "one-light", transformers }),
    codeToHtml(code, { lang: language, theme: "one-dark-pro", transformers }),
  ]);
}

/** Displays upstream AI Elements-style code with an accessible plain-text fallback while highlighting. */
export const CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  className,
  children,
  ...props
}: CodeBlockProps) => {
  const [html, setHtml] = useState("");
  const [darkHtml, setDarkHtml] = useState("");
  useEffect(() => {
    let active = true;
    // Shiki is an external highlighter; retain a plain-text fallback when it is unavailable (including jsdom).
    void highlightCode(code, language, showLineNumbers)
      .then(([light, dark]) => {
        if (active) {
          setHtml(light);
          setDarkHtml(dark);
        }
      })
      .catch(() => {
        if (active) {
          setHtml("");
          setDarkHtml("");
        }
      });
    return () => {
      active = false;
    };
  }, [code, language, showLineNumbers]);
  return (
    <CodeBlockContext.Provider value={{ code }}>
      <div
        className={cn(
          "group relative w-full overflow-hidden rounded-md border bg-background text-foreground",
          className,
        )}
        {...props}
      >
        <div className="relative">
          {html ? (
            <>
              <div
                className="overflow-auto dark:hidden [&>pre]:m-0 [&>pre]:bg-background! [&>pre]:p-4 [&>pre]:text-foreground! [&>pre]:text-sm [&_code]:font-mono [&_code]:text-sm"
                dangerouslySetInnerHTML={{ __html: html }}
              />
              <div
                className="hidden overflow-auto dark:block [&>pre]:m-0 [&>pre]:bg-background! [&>pre]:p-4 [&>pre]:text-foreground! [&>pre]:text-sm [&_code]:font-mono [&_code]:text-sm"
                dangerouslySetInnerHTML={{ __html: darkHtml }}
              />
            </>
          ) : (
            <pre className="overflow-auto p-4 text-sm">
              <code>{code}</code>
            </pre>
          )}
          {children && (
            <div className="absolute top-2 right-2 flex items-center gap-2">{children}</div>
          )}
        </div>
      </div>
    </CodeBlockContext.Provider>
  );
};

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

/** Copies the surrounding {@link CodeBlock} source to the clipboard. */
export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { code } = useContext(CodeBlockContext);
  const copyToClipboard = async () => {
    if (!globalThis.navigator?.clipboard?.writeText) {
      onError?.(new Error("Clipboard API not available"));
      return;
    }
    try {
      await globalThis.navigator.clipboard.writeText(code);
      setIsCopied(true);
      onCopy?.();
      setTimeout(() => setIsCopied(false), timeout);
    } catch (cause) {
      onError?.(parseError(cause));
    }
  };
  const Icon = isCopied ? CheckIcon : CopyIcon;
  return (
    <Button
      className={cn("shrink-0", className)}
      onClick={copyToClipboard}
      size="icon"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon size={14} />}
    </Button>
  );
};
